/**
 * Exporta a XLSX el Reporte de Captura de Calificaciones.
 * Fuente de datos: Rúbrica.
 *
 * @param {Object} params
 * @param {Array}  params.resultados  - Filas con: asesor, campus, seccion, programa, clave_grupo, materia,
 *                                      total_estudiantes, total_calificados, total_pendientes, porcentaje,
 *                                      estado_captura, fecha_actualizacion
 * @param {Object} params.contexto    - { ciclo, campus, seccion, parcial }
 */
import * as XLSX from 'xlsx';

const COLUMNAS = [
  'Asesor',
  'Campus',
  'Sección',
  'Programa',
  'Clave de Grupo',
  'Materia',
  'Total de Alumnos',
  'Alumnos Evaluados',
  'Alumnos Pendientes',
  '% de Captura',
  'Estado de Captura',
  'Última Actualización',
];

const ESTADO_LABELS = {
  COMPLETO: 'Completo',
  PARCIAL: 'Parcial',
  SIN_CAPTURA: 'Sin captura',
  SIN_ALUMNOS: 'Sin alumnos',
};

function fechaArchivo() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function formatFecha(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('es-MX', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function exportReporteCapturaXLS({ resultados = [], contexto = {} } = {}) {
  const {
    ciclo = 'Todos',
    campus = 'Todos',
    seccion = 'Todas',
    parcial = '',
  } = contexto;

  const aoa = [
    ['REPORTE DE CAPTURA DE CALIFICACIONES'],
    [`Ciclo Escolar: ${ciclo}`],
    [`Campus: ${campus}`],
    [`Sección: ${seccion}`],
    [`Parcial: ${parcial}`],
    ['Fuente: Rúbrica'],
    [`Generado: ${new Date().toLocaleString('es-MX')}`],
    [],
    COLUMNAS,
  ];

  resultados.forEach((r) => {
    const total = Number(r.total_estudiantes || 0);
    const calif = Number(r.total_calificados || 0);
    const pendientes = r.total_pendientes != null
      ? Number(r.total_pendientes)
      : Math.max(0, total - calif);

    aoa.push([
      r.asesor || '',
      r.campus || '',
      r.seccion || '',
      r.programa || '',
      r.clave_grupo || '',
      r.materia || '',
      total,
      calif,
      pendientes,
      `${Number(r.porcentaje || 0).toFixed(2)}%`,
      ESTADO_LABELS[r.estado_captura] || r.estado_captura || '',
      formatFecha(r.fecha_actualizacion),
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = [
    { wch: 32 }, // Asesor
    { wch: 16 }, // Campus
    { wch: 16 }, // Sección
    { wch: 30 }, // Programa
    { wch: 16 }, // Clave Grupo
    { wch: 36 }, // Materia
    { wch: 12 }, // Total
    { wch: 14 }, // Evaluados
    { wch: 14 }, // Pendientes
    { wch: 12 }, // %
    { wch: 16 }, // Estado
    { wch: 20 }, // Fecha
  ];

  // Combinar la celda de título sobre todas las columnas
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: COLUMNAS.length - 1 } },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Captura');

  const safeParcial = (parcial || 'parcial').toLowerCase();
  const fileName = `reporte_captura_calificaciones_${safeParcial}_${fechaArchivo()}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

export default exportReporteCapturaXLS;
